import React, { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import { Select } from '../../../../../../../../CommonComponents/OPL/Select/Select/Select';
import {
  accountPortfolioDataToForm,
  AnnuityType,
  DEFAULT_RETIREMENT_ACCOUNTS,
  FormFields,
  getDefaultContribution,
  ROTH_ACCOUNT_TYPES,
} from '../../AccountForm.meta';
import { useAccountFormContext } from '../../context/useAccountFormContext';
import { BalanceUnitDTO, TaxStatusDTO } from '../../../../../../api/useAccountsQuery';
import {
  AccountType,
  DefaultAssetBucketingIndicesDTO,
  ManagementStyleDTO,
} from '../../../../../../api/useAccountsOptionsQuery';
import { EXCLUDE_FROM_ASSET_BUCKETING_OPTION_VALUE } from '../AssetBucketing/AssetBucketing.meta';
import { AccountModalUsageContext } from '../../context/AccountFormProvider.meta';

const getDefaultAssetBucketingIndex = ({
  accountTypeSelected,
  assetBucketingByManagementStyle,
  defaultAssetBucketingIndices,
  managementStyleOptions,
  orionManagedStyleId,
}: {
  accountTypeSelected: AccountType;
  assetBucketingByManagementStyle: boolean;
  defaultAssetBucketingIndices: DefaultAssetBucketingIndicesDTO[];
  managementStyleOptions: ManagementStyleDTO[];
  orionManagedStyleId: number | null;
}) => {
  if (assetBucketingByManagementStyle) {
    const selectedManagementStyleOption = managementStyleOptions.find(
      option => option.id === orionManagedStyleId,
    );

    return (
      selectedManagementStyleOption?.defaultBucket ?? EXCLUDE_FROM_ASSET_BUCKETING_OPTION_VALUE
    );
  }

  return (
    defaultAssetBucketingIndices[AccountType[accountTypeSelected]] ??
    EXCLUDE_FROM_ASSET_BUCKETING_OPTION_VALUE
  );
};

interface AccountTypeOptions {
  label: string;
  options: { value: AccountType; label: string }[];
}

const ACCOUNT_TYPE_OPTIONS = [
  {
    label: 'Bank Accounts',
    options: [
      { value: AccountType.Checking, label: 'Checking' },
      { value: AccountType.Savings, label: 'Savings Account' },
      { value: AccountType.CD, label: 'CD' },
    ],
  },
  {
    label: 'Investment Accounts',
    options: [
      { value: AccountType.Brokerage, label: 'Investment' },
      { value: AccountType.IRA, label: 'Traditional IRA' },
      { value: AccountType.Roth, label: 'Roth IRA' },
      { value: AccountType.InheritedIra, label: 'Inherited IRA' },
      { value: AccountType.InheritedRoth, label: 'Inherited Roth IRA' },
      { value: AccountType.Annuity, label: 'Annuity' },
      { value: AccountType.E529, label: '529 Plan' },
      { value: AccountType.TaxFree, label: 'Tax-Free Account' },
    ],
  },
  {
    label: 'Employer-Sponsored Accounts',
    options: [
      { value: AccountType.SepIra, label: 'SEP IRA' },
      { value: AccountType.SimpleIra, label: 'SIMPLE IRA' },
      { value: AccountType.EmployerPlan, label: 'Employer Plan' },
      { value: AccountType.E401K, label: '401(k)' },
      { value: AccountType.Roth401K, label: 'Roth 401(k)' },
      { value: AccountType.E401A, label: '401(a)' },
      { value: AccountType.E403B, label: '403(b)' },
      { value: AccountType.E457, label: '457 Plan' },
    ],
  },
  {
    label: 'Loans & Credit Cards',
    options: [
      { value: AccountType.CreditCard, label: 'Credit Card' },
      { value: AccountType.Mortgage, label: 'Mortgage' },
      { value: AccountType.AutoLoan, label: 'Auto Loan' },
      { value: AccountType.Installment, label: 'Installment' },
      { value: AccountType.LineOfCredit, label: 'Line of Credit' },
      { value: AccountType.StudentLoan, label: 'Student Loan' },
    ],
  },
  {
    label: 'Other',
    options: [
      { value: AccountType.HSA, label: 'Health Savings Account' },
      { value: AccountType.TaxDeferred, label: 'Tax-Deferred Account' },
      { value: AccountType.DonorAdvisedFund, label: 'Donor Advised Fund' },
    ],
  },
] as AccountTypeOptions[];

export const AccountTypeSelect = () => {
  const formBag = useFormContext<FormFields>();

  const { accountId, options: accountOptions, setAlert, usageContext } = useAccountFormContext();

  const {
    EarmarkOptions: earmarkOptions,
    AccountTypeOptions,
    DefaultCapitalGainsRatio: defaultCapitalGainsRatio,
    DefaultTurnoverRatio: defaultTurnoverRatio,
    DefaultPercentageUsedInPlan: defaultPercentageUsedInPlan,
    DefaultAssetBucketingIndices: defaultAssetBucketingIndices,
    AssetBucketingByManagementStyle: assetBucketingByManagementStyle,
    ManagementStyles: managementStyleOptions,
    OwnerOptions: ownerOptions,
  } = accountOptions;

  const accountTypeOptions = useMemo(() => {
    const baseAccountTypesByKey = ACCOUNT_TYPE_OPTIONS.reduce((acc, { label, options }) => {
      return {
        ...acc,
        ...options.reduce(
          (accOption, option) => ({
            ...accOption,
            [option.value]: { parentLabel: label, label: option.label },
          }),
          {},
        ),
      };
    }, {} as { [key in AccountType]: { parentLabel: string; label: string } });

    const accountTypesMap = AccountTypeOptions.reduce((acc, item) => {
      const select = baseAccountTypesByKey[item.AccountType];

      if (!select) {
        return acc;
      }

      return {
        ...acc,
        [select.parentLabel]: {
          ...(acc[select.parentLabel] ? acc[select.parentLabel] : {}),
          label: select.parentLabel,
          options: [
            ...(acc[select.parentLabel]?.options ? acc[select.parentLabel]?.options : []),
            { value: item.AccountType, label: select.label },
          ],
        },
      };
    }, {} as { [key: string]: AccountTypeOptions });

    return Object.values(accountTypesMap);
  }, [AccountTypeOptions]);

  const contributions = useWatch({ name: 'bankAccount.Contributions', control: formBag.control });
  const isBypassRothIraIncomeLimits = useWatch({
    name: 'bankAccount.IsBypassRothIraIncomeLimits',
    control: formBag.control,
  });

  useEffectOnce(() => {
    if (usageContext === AccountModalUsageContext.AnnuityPurchaseWhatIf) {
      formBag.setValue('bankAccount.AccountType', AccountType.Annuity);
    }
  });

  return (
    <Controller
      control={formBag.control}
      name="bankAccount.AccountType"
      render={({ field: { value, onChange, ref } }) => (
        <Select
          innerRef={ref}
          value={accountTypeOptions
            .map(({ options }) => options)
            .flat()
            .filter(option => option.value === value)}
          options={accountTypeOptions}
          onChange={opt => {
            if (opt) {
              if (opt.value !== AccountType.Roth && isBypassRothIraIncomeLimits) {
                formBag.setValue('bankAccount.IsBypassRothIraIncomeLimits', false);
              }

              const accountPortfolio = accountPortfolioDataToForm({
                accountType: opt.value,
                accountOptions,
              });

              // assign assetAllocationList only if the value isn't already set
              // (for new account or if Asset Allocation tab is newly added)
              if (accountPortfolio && !formBag.getValues('accountPortfolio.assetAllocationList')) {
                formBag.setValue(
                  'accountPortfolio.assetAllocationList',
                  accountPortfolio.assetAllocationList,
                );
              }

              const defaultTaxStatus = ROTH_ACCOUNT_TYPES.includes(opt.value)
                ? TaxStatusDTO.PreTax
                : null;

              formBag.setValue('bankAccount.TaxStatus', defaultTaxStatus);
              formBag.setValue('bankAccount.OwnerModel', [
                { ...ownerOptions.People[0], Percentage: '100' },
              ]);

              if (DEFAULT_RETIREMENT_ACCOUNTS.includes(opt.value)) {
                formBag.setValue(
                  'bankAccount.AccountEarmark',
                  earmarkOptions.filter(earmark => earmark.value === 'Retirement'),
                );
              }

              const accountTypeOption = AccountTypeOptions.find(
                option => option.AccountType === opt.value,
              );

              const defaultAssetBucketingIndex = getDefaultAssetBucketingIndex({
                accountTypeSelected: opt.value,
                assetBucketingByManagementStyle,
                defaultAssetBucketingIndices,
                managementStyleOptions,
                orionManagedStyleId: formBag.getValues('bankAccount.OrionManagementStyleId'),
              });

              formBag.setValue('bankAccount.CustomAssetBucketIndex', defaultAssetBucketingIndex);

              if (accountTypeOption) {
                formBag.setValue('bankAccount.IsLiquid', accountTypeOption.IsLiquid);

                const balanceData = formBag.getValues('bankAccount.Balances');

                if (accountTypeOption.BalanceTaxStatuses.length > 0) {
                  formBag.setValue('bankAccount.Balances', {
                    ...accountTypeOption.BalanceTaxStatuses.reduce((acc, values) => {
                      if (values.Value === TaxStatusDTO.Mix) {
                        return acc;
                      }

                      const normalizedTaxStatusKey = TaxStatusDTO[values.Value];

                      return {
                        ...acc,

                        [normalizedTaxStatusKey]: {
                          ...acc[normalizedTaxStatusKey],
                          Earmarks: acc[normalizedTaxStatusKey]?.Earmarks || [],
                          AssetBucketIndex:
                            acc[normalizedTaxStatusKey]?.AssetBucketIndex ??
                            values.Value === defaultTaxStatus
                              ? defaultAssetBucketingIndex
                              : null,
                        },
                      };
                    }, balanceData || {}),
                    Unit: BalanceUnitDTO.Currency,
                  });
                }
              }

              if (opt.value === AccountType.Brokerage) {
                formBag.setValue('bankAccount.TurnoverRatio', defaultTurnoverRatio);
                formBag.setValue('bankAccount.CapitalGainsRatio', defaultCapitalGainsRatio);
              }

              if (
                [
                  AccountType.Brokerage,
                  AccountType.E401K,
                  AccountType.Roth401K,
                  AccountType.E401A,
                  AccountType.E403B,
                  AccountType.E457,
                ].includes(opt.value)
              ) {
                formBag.setValue('bankAccount.PercentageUsedInPlan', defaultPercentageUsedInPlan);
              }

              if (opt.value === AccountType.Mortgage) {
                formBag.setValue('bankAccount.ExcludeFromDebtManagementGoal', true);
              }

              if (opt.value === AccountType.Annuity) {
                formBag.setValue('bankAccount.AnnuityType', AnnuityType.Fixed);
                formBag.setValue('bankAccount.AnnuitizationStartYear', -1);
              } else {
                formBag.setValue('bankAccount.AnnuityType', null);
              }

              if (!accountId) {
                formBag.setValue('bankAccount.Contributions', [getDefaultContribution(opt.value)]);
              }

              if (!ROTH_ACCOUNT_TYPES.includes(opt.value) && contributions.length > 1) {
                setAlert({
                  message:
                    'The current account type does not support multiple contributions. All existing contributions will be removed.',
                  type: 'warning',
                });

                formBag.setValue('bankAccount.Contributions', [getDefaultContribution(opt.value)]);
              }

              // Some account types do not support maximized contributions.
              // If the selected account type doesn't support maximization and any contribution is set to "maximize",
              // reset contributions to the default value.
              if (accountTypeOption && !accountTypeOption.CanEmployeeMaxContribution) {
                if ((contributions || []).some(({ PersonalMaximize }) => PersonalMaximize)) {
                  formBag.setValue('bankAccount.Contributions', [
                    getDefaultContribution(opt.value),
                  ]);
                }
              }
            }

            onChange(opt?.value);
          }}
          label="Account type"
          placeholder="Account Type"
          isPortal
        />
      )}
    />
  );
};
